import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Aperture, Loader2, MailCheck } from "lucide-react";

const AUTH_IMG = "https://images.pexels.com/photos/19923619/pexels-photo-19923619.jpeg";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="photographer" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Back in focus.</h2>
          <p className="text-neutral-300 mt-3 max-w-md">We'll send a secure link to reset your password.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <Aperture className="h-7 w-7 text-amber-glow" strokeWidth={1.5} />
            <span className="font-display text-2xl">Aevolux</span>
          </div>

          {sent ? (
            <div data-testid="forgot-success">
              <MailCheck className="h-10 w-10 text-amber-glow mb-5" strokeWidth={1.5} />
              <h1 className="font-display text-3xl mb-3">Check your inbox</h1>
              <p className="text-neutral-400 leading-relaxed">
                If an account exists for <span className="text-white">{email}</span>, a password reset link is on its way. The link expires in 1 hour.
              </p>
              <Link to="/login" data-testid="back-to-login" className="inline-block mt-8 text-amber-glow hover:underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-500">Recover access</p>
              <h1 className="font-display text-4xl mt-2 mb-8">Forgot password</h1>

              {error && (
                <div data-testid="forgot-error" className="mb-5 text-sm text-red-400 bg-red-950/40 border border-red-900/50 px-4 py-3 rounded-sm">
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase text-neutral-400">Email</label>
                  <Input
                    data-testid="forgot-email-input"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 bg-[#121212] border-white/10 h-12 rounded-sm focus-visible:ring-amber-glow"
                    placeholder="you@studio.com"
                  />
                </div>
                <Button
                  data-testid="forgot-submit-button"
                  type="submit" disabled={loading}
                  className="w-full h-12 bg-white text-black font-semibold hover:bg-neutral-200 rounded-sm"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>

              <p className="text-sm text-neutral-400 mt-6">
                Remembered it?{" "}
                <Link to="/login" data-testid="goto-login" className="text-amber-glow hover:underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
